/**
 * Notes, tasks, calls and meetings, the tasks view and record timelines, as
 * TanStack Query hooks.
 *
 * Every write changes what a timeline, the tasks list and the badge show, so
 * every write invalidates all three. A timeline is paged newest first with
 * the server's cursor, so it is an infinite query: "Show older" fetches the
 * next page and keeps the ones already on screen.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import type { components, paths } from './schema';

type S = components['schemas'];
export type Activity = S['Activity'];
export type ActivityKind = S['ActivityKind'];
export type ActivityObjectType = S['ActivityObjectType'];
export type ActivityLink = S['ActivityLink'];
export type TimelineItem = S['TimelineItem'];
export type TimelineItemKind = S['TimelineItemKind'];
export type TimelineLink = S['TimelineLink'];
export type TimelineResponse = S['TimelineResponse'];
export type TaskListResponse = S['TaskListResponse'];
export type TaskCounts = S['TaskCounts'];
export type CreateActivityBody = S['CreateActivityBody'];
export type UpdateActivityBody = S['UpdateActivityBody'];
export type TaskListParams = NonNullable<paths['/api/v1/tasks']['get']['parameters']['query']>;

/** Everything an activity write can change on screen. */
function useInvalidateActivities() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['timeline'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['task-counts'] });
    qc.invalidateQueries({ queryKey: ['activities'] });
  };
}

export function useTimeline(objectType: ActivityObjectType, objectId: string | null, limit = 30) {
  return useInfiniteQuery({
    queryKey: ['timeline', objectType, objectId, limit],
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/timeline/{object_type}/{object_id}', {
          params: {
            path: { object_type: objectType, object_id: objectId! },
            query: pageParam ? { before: pageParam, limit } : { limit },
          },
        }),
        'TimelineResponse',
      ),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!objectId,
    staleTime: 15 * 1000,
  });
}

export function useTasks(params: TaskListParams, enabled = true) {
  const query = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  ) as TaskListParams;
  return useQuery({
    queryKey: ['tasks', query],
    queryFn: async () => unwrap(await api.GET('/api/v1/tasks', { params: { query } }), 'TaskListResponse'),
    staleTime: 15 * 1000,
    enabled,
  });
}

/**
 * My open task counts, for the badge. Asks for one row: only the counts are
 * read. Refreshed every minute, so a task falling overdue shows without a
 * reload.
 */
export function useTaskCounts(enabled = true) {
  return useQuery({
    queryKey: ['task-counts'],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/tasks', { params: { query: { owner: 'me', page_size: 1 } } }),
        'TaskListResponse',
      ).counts,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    enabled,
  });
}

export function useCreateActivity() {
  const invalidate = useInvalidateActivities();
  return useMutation({
    mutationFn: async (body: CreateActivityBody) =>
      unwrap(await api.POST('/api/v1/activities', { body }), 'Activity'),
    onSuccess: invalidate,
  });
}

export function useUpdateActivity() {
  const invalidate = useInvalidateActivities();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateActivityBody }) =>
      unwrap(await api.PATCH('/api/v1/activities/{id}', { params: { path: { id } }, body }), 'Activity'),
    onSuccess: invalidate,
  });
}

export function useDeleteActivity() {
  const invalidate = useInvalidateActivities();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/api/v1/activities/{id}', { params: { path: { id } } });
      if (res.error !== undefined) {
        const e = res.error as { detail?: string };
        throw new Error(e.detail ?? 'Delete failed');
      }
      return id;
    },
    onSuccess: invalidate,
  });
}

/** Tick a task, or untick it. */
export function useSetTaskDone() {
  const invalidate = useInvalidateActivities();
  return useMutation({
    mutationFn: async ({ id, done }: { id: string; done: boolean }) =>
      unwrap(
        done
          ? await api.POST('/api/v1/activities/{id}/complete', { params: { path: { id } } })
          : await api.POST('/api/v1/activities/{id}/reopen', { params: { path: { id } } }),
        'Activity',
      ),
    onSuccess: invalidate,
  });
}
